/** THE HELPER DOCK — where the helper panel lives, and in which shape.
 *
 *  Mounted ONCE above the routes, so a page change never closes the panel,
 *  never reloads it and never clears the conversation or a half-typed
 *  question. Everything a page could destroy stays here.
 *
 *  Modes:
 *    closed   nothing on screen but the Help & Insights entry (today)
 *    overlay  today's pop-up: dimmed backdrop, portal not usable behind it
 *    pinned   docked to the right edge, NO dim and NO blur; the portal is
 *             narrowed to sit beside it and stays fully usable
 *    slim     a thin bar on the right edge — never fully closed, one tap back
 *
 *  Remembered per user, per device, so a reload or a fresh sign-in on the same
 *  iPad comes back the way it was left.
 *
 *  Voice later: the panel is never unmounted while pinned or slim, so a
 *  microphone and a read-aloud toggle added INSIDE it will keep working across
 *  page changes. Nothing about voice is built here.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from './api';
import { useAuth } from './auth';

const HelperDockContext = createContext(null);

export const PHONE_MAX = 600;        // under this the dock is a bottom sheet
const KEY = 'yot_helper_dock';

/** A sensible fixed width: roomy on a desktop, still leaving a usable portal
 *  on an iPad in portrait. Under PHONE_MAX there is no side dock at all. */
export const dockWidth = (w) => {
    if (w < PHONE_MAX) return 0;
    return w >= 1100 ? 380 : 320;
};

/** The showroom demo tab renders its own shell with no portal chrome at all —
 *  the helper has never appeared there and must not start to. */
const inShowroomDemo = () => {
    try {
        return window.sessionStorage.getItem('yulontap_demo_mode') === '1';
    } catch (_e) { return false; }
};

const readStored = (uid) => {
    try {
        const raw = window.localStorage.getItem(`${KEY}:${uid || 'anon'}`);
        return ['closed', 'overlay', 'pinned', 'slim'].includes(raw) ? raw : null;
    } catch (_e) { return null; }
};

export const HelperDockProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const uid = user?.id || user?.email || 'anon';
    const [mode, setMode] = useState('closed');
    const [micPing, setMicPing] = useState(0);
    const [allowed, setAllowed] = useState(false);
    const [width, setWidth] = useState(() => (typeof window === 'undefined'
        ? 1280 : window.innerWidth));

    // The tick and the Hide-while-I-present switch, read once per sign-in.
    useEffect(() => {
        if (!isAuthenticated || inShowroomDemo()) { setAllowed(false); return undefined; }
        let alive = true;
        api.get('/helper/config')
            .then((res) => {
                if (!alive) return;
                setAllowed(Boolean(res.data.allowed) && !res.data.hide_in_demo);
            })
            .catch(() => setAllowed(false));
        return () => { alive = false; };
    }, [isAuthenticated, uid]);

    // What this person left it as, on THIS device.
    useEffect(() => {
        if (!isAuthenticated) return;
        setMode(readStored(uid) || 'closed');
    }, [isAuthenticated, uid]);

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);

    const isPhone = width < PHONE_MAX;
    const docked = allowed && (mode === 'pinned' || mode === 'slim');

    // THE ONE NUMBER THE PORTAL READS. The shell narrows by exactly this, so
    // nothing is covered and nothing is dimmed.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const px = (docked && !isPhone)
            ? (mode === 'slim' ? 48 : dockWidth(width)) : 0;
        document.documentElement.style.setProperty('--helper-dock-w', `${px}px`);
        return () => {
            document.documentElement.style.setProperty('--helper-dock-w', '0px');
        };
    }, [docked, isPhone, mode, width]);

    const remember = useCallback((next) => {
        setMode(next);
        try { window.localStorage.setItem(`${KEY}:${uid || 'anon'}`, next); }
        catch (_e) { /* a private window simply does not remember */ }
    }, [uid]);

    const value = useMemo(() => ({
        allowed,
        mode,
        isPhone,
        width: (mode === 'pinned' && !isPhone) ? dockWidth(width) : 0,
        open: () => remember('overlay'),
        close: () => remember('closed'),
        pin: () => remember('pinned'),
        unpin: () => remember('overlay'),
        slim: () => remember('slim'),
        restore: () => remember('pinned'),
        micPing,
        askMic: () => { remember('pinned'); setMicPing((n) => n + 1); },
        toggle: () => remember(mode === 'closed' ? 'overlay' : 'closed'),
    }), [allowed, mode, isPhone, width, remember, micPing]);

    return (
        <HelperDockContext.Provider value={value}>
            {children}
        </HelperDockContext.Provider>
    );
};

export const useHelperDock = () => useContext(HelperDockContext) || {
    allowed: false, mode: 'closed', isPhone: false, width: 0, micPing: 0,
    open: () => {}, close: () => {}, pin: () => {}, unpin: () => {},
    slim: () => {}, restore: () => {}, askMic: () => {}, toggle: () => {},
};

export default HelperDockProvider;
